import { isValidDate } from "utils/date";
import type { OpenCall, OpenCallTag } from "../types";

/**
 * Returns an array of tags based on an OpenCall's properties and the current date
 *
 * NOTE: this only controls the Tag badge component, there are more nuanced controls for how an
 * Open Call is displayed on its details page (e.g. which CTA button is showing?)
 *
 * Reference:
 * https://docs.google.com/presentation/d/1Q3jgr55X5s5HMNyuqF31pcw0RUQgTIa4439oMr1TFyk/edit#slide=id.g25a6455a030_0_3
 */
function getOpenCallTag(openCall: OpenCall): OpenCallTag {
  const now = new Date();
  const submissionsOpen = new Date(openCall.submissionStartDate);

  // Submissions haven't opened yet
  if (submissionsOpen > now) {
    return "Coming Soon";
  }

  // Submissions have opened and haven't closed yet
  const submissionsClosed = new Date(openCall.submissionEndDate ?? "");
  if (
    !openCall.submissionEndDate ||
    (isValidDate(submissionsClosed) && submissionsClosed > now)
  ) {
    return "Submissions Open";
  }

  // Submissions are closed, community voting is enabled, and voting has not started yet.
  const votingStart = new Date(openCall.communityVotingStartDate ?? "");
  if (
    openCall.isCommunityCuration &&
    isValidDate(votingStart) &&
    votingStart > now
  ) {
    return "Voting Soon";
  }

  // Community voting is currently open.
  const votingEnd = new Date(openCall.communityVotingEndDate ?? "");
  if (
    openCall.isCommunityCuration &&
    isValidDate(votingEnd) &&
    votingEnd > now
  ) {
    return "Voting Open";
  }

  // Submissions are closed, submissions haven't been selected yet
  if (!openCall.isSelectionCompleted) {
    return "Under Curation";
  }

  // Selections made, exhibition is enabled and hasn't opened yet
  const exhibitionStart = new Date(openCall.exhibitionStartDate ?? "");
  if (isValidDate(exhibitionStart) && exhibitionStart > now) {
    return "Artists Selected";
  }

  // Selections made, exhibition is enabled and hasn't closed yet
  const exhibitionEnd = new Date(openCall.exhibitionEndDate ?? "");
  if (isValidDate(exhibitionEnd) && exhibitionEnd > now) {
    return "On Display";
  }

  // Selections made, exhibition has closed or wasn't enabled
  return "Concluded";
}

export { getOpenCallTag };
